/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, Validators } from '@angular/forms';
import { HealthChecksApiService, Layer4ApiService } from '../../../../http';
import { L4BackendServiceModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService } from '../../../../services/components';
import { nameValidation, portRangeValidation } from '../../../../../../../core/directives';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-lb-layer4-backend',
    templateUrl: './layer4-backend.component.html',
    styleUrls: ['./layer4-backend.component.scss']
})

export class Layer4BackendComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Reference to embedded component container.
     */
    @ViewChild('addEditContainer', { read: ViewContainerRef, static: true })
    public addEditContainer: any;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        balanceModes: [],
        checkScripts: [],
        healthChecks: [],
        protocols: [],
        feedbackMethods: [],
        forwardingModes: []
    };

    /**
     * A boolean to determine if the form should be full width or not.
     */
    public isFormFullWidth: boolean = false;

    /**
     * List of backends
     * @private
     */
    private backends: L4BackendServiceModel[] = [];

    /**
     * Store the visibility state of the healthcheck options.
     * @private
     */
    private healthCheckFieldsLookup: any = {};

    constructor(
        private healthCheckScriptsApiService: HealthChecksApiService,
        private layer4ApiService: Layer4ApiService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
        this.componentTitle = WizardTitleEnum.BACKENDS;
    }

    ngOnInit() {
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Set Rip ids in the form.
     * @param event
     */
    public setRipIds(event: any): void {
        this.serviceForm.get(WizardSubStepTitlesEnum.RIP_IDS + '.ripIds').setValue(event);
    }

    /**
     * Set the output of the fallback server options component to the form.
     * @param $event
     */
    public setFallbackServerConfig($event: any): void {
        const { fallbackServerId, fallbackServerForwardingMode } = $event;
        this.serviceForm.get(WizardSubStepTitlesEnum.FALLBACK + '.fallbackServerId').setValue(fallbackServerId);
        this.serviceForm.get(WizardSubStepTitlesEnum.FALLBACK + '.fallbackServerForwardingMode').setValue(
            fallbackServerForwardingMode
        );
    }

    /**
     * Set up the form for the view.
     * @private
     */
    private setupFormGroup(): void {
        // Define our form builder.
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                id: [''], // <- Used outside this component.
                name: ['', [nameValidation, Validators.required]],
                balanceMode: ['leastConnections'],
                forwardingMode: ['sourceNat']
            }),
            [WizardSubStepTitlesEnum.HEALTH]: this.formBuilder.group({
                healthCheck: ['connectToPort'],
                healthCheckPort: [null],
                healthCheckRequestToSend: ['/', Validators.required],
                healthCheckResponseExpected: ['OK', Validators.required],
                healthCheckUsername: [null, Validators.required],
                healthCheckPassword: [null, Validators.required],
                healthCheckScriptId: [null, Validators.required],
                healthCheckHostHeader: ['', Validators.required],
                healthCheckDatabaseName: [null, Validators.required],
                healthCheckRadiusSecret: [null, Validators.required]
            }),
            [WizardSubStepTitlesEnum.FEEDBACK]: this.formBuilder.group({
                feedbackMethod: ['none', Validators.required],
                feedbackAgentPort: [3333, portRangeValidation],
            }),
            [WizardSubStepTitlesEnum.PERSISTENCE]: this.formBuilder.group({
                persistenceEnabled: [true],
                persistenceTimeout: ['5m', Validators.required],
                granularity: [128, Validators.required],
            }),
            [WizardSubStepTitlesEnum.FALLBACK]: this.formBuilder.group({
                fallbackServerId: [''],
                fallbackServerForwardingMode: ['sourceNat', null]
            }),
            [WizardSubStepTitlesEnum.RIP_IDS]: this.formBuilder.group({
                ripIds: [[], Validators.required], // <- Used in real server component.
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.BACKENDS);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        // Set up our form value changes subscriber
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    // Validate the form each step of the way. Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('backends', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.GENERAL + '.name').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (name: string): void => {
                    this.checkNameForDuplicates(name, this.backends);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE + '.persistenceEnabled').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (enabled: boolean): void => {
                    this.togglePersistenceFields(enabled);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheck').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (healthCheck: string): void => {
                    this.toggleHealthCheckInputs(healthCheck);
                }
            });

        this.serviceForm.get(WizardSubStepTitlesEnum.FEEDBACK + '.feedbackMethod').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (feedbackMethod: string): void => {
                    this.toggleFeedbackPort(feedbackMethod);
                }
            });
    }

    /**
     * Toggle persistence fields.
     * @param enabled
     * @private
     */
    private togglePersistenceFields(enabled: boolean): void {
        const persistenceController = this.serviceForm.get(WizardSubStepTitlesEnum.PERSISTENCE);
        this.updateFormInputState(persistenceController, this.inputStateStore,
            ['persistenceTimeout', 'granularity'], !enabled);
    }

    /**
     * Toggle health check input fields.
     * @param healthCheck
     * @private
     */
    private toggleHealthCheckInputs(healthCheck: string): void {
        let visibleFields: string[] = [];
        switch (healthCheck) {
            case 'externalScript':
                visibleFields = ['healthCheckPort', 'healthCheckScriptId'];
                break;
            case 'negotiateDns':
            case 'negotiateHttpProxy':
            case 'negotiateSimpleTcp':
                visibleFields = ['healthCheckPort', 'healthCheckRequestToSend', 'healthCheckResponseExpected'];
                break;
            case 'negotiateFtp':
                visibleFields = ['healthCheckPort',
                    'healthCheckRequestToSend', 'healthCheckResponseExpected',
                    'healthCheckUsername', 'healthCheckPassword'
                ];
                break;
            case 'negotiateHttp':
            case 'negotiateHttps':
                visibleFields = ['healthCheckPort', 'healthCheckRequestToSend',
                    'healthCheckResponseExpected', 'healthCheckHostHeader'];
                break;
            case 'negotiateImap':
            case 'negotiateImaps':
            case 'negotiateLdap':
            case 'negotiatePop':
            case 'negotiatePops':
            case 'negotiateSip':
                visibleFields = ['healthCheckPort', 'healthCheckUsername', 'healthCheckPassword'];
                break;
            case 'negotiateMysql':
                visibleFields = ['healthCheckPort', 'healthCheckRequestToSend', 'healthCheckDatabaseName',
                    'healthCheckUsername', 'healthCheckPassword'];
                break;
            case 'negotiateNntp':
            case 'negotiateSmtp':
            case 'connectToPort':
                visibleFields = ['healthCheckPort'];
                break;
            case 'negotiateRadius':
                visibleFields = ['healthCheckPort', 'healthCheckRadiusSecret',
                    'healthCheckUsername', 'healthCheckPassword'];
                break;
        }

        this.setVisibleFieldsInLookup(visibleFields);
    }

    /**
     * Set the visibility of the healthcheck fields in the lookup.
     * @param visibleFields
     * @private
     */
    private setVisibleFieldsInLookup(visibleFields: string[]): void {
        visibleFields.push('healthCheck');
        const healthController = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH);
        for (const field in this.healthCheckFieldsLookup) {
            if (this.healthCheckFieldsLookup.hasOwnProperty(field)) {
                const fieldVisible: boolean = visibleFields.includes(field);
                this.updateFormInputState(healthController, this.inputStateStore, [field], !fieldVisible, false);
                this.healthCheckFieldsLookup[field] = { hidden: !fieldVisible };
            }
        }

        this.toggleAdvanceModeInputs();
    }

    /**
     * Toggle the feedback agent port.
     * @param feedbackMethod
     * @private
     */
    private toggleFeedbackPort(feedbackMethod: string): void {
        const feedbackController = this.serviceForm.get(WizardSubStepTitlesEnum.FEEDBACK);
        this.updateFormInputState(feedbackController, this.inputStateStore, ['feedbackAgentPort'], feedbackMethod !== 'agent');
    }

    /**
     * Set initial values in health check lookup.
     * @param template
     * @private
     */
    private setInitialHealthCheckLookup(template: any): void {
        const controls = this.serviceForm.get(this.subStepEnum.HEALTH)['controls'];
        for (const key in controls) {
            if (controls.hasOwnProperty(key)) {
                this.healthCheckFieldsLookup[key] = {
                    hidden: template[key]?.hidden
                };
            }
        }

        this.healthCheckFieldsLookup['healthCheck'].hidden = false;
    }

    /**
     * Setup wizard subscribers
     * @private
     */
    private setupWizardSubscribers(): void {
        /**
         * Advanced Mode Subscriber
         * Note: We subscribe to whether the advanced mode button has been toggled.
         */
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (advancedState: string): void => {
                    this.advancedMode = advancedState;
                    this.toggleAdvanceModeSteps();
                    this.toggleAdvanceModeInputs();
                }
            });

        this.wizardService.subStepNameEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(step => {
                const fullWithPages: string[] = [
                    WizardSubStepTitlesEnum.HEADERS, WizardSubStepTitlesEnum.RIP_IDS
                ];

                this.isFormFullWidth = fullWithPages.includes(step);
            });
    }

    /**
     * Sets advance mode steps.
     * @private
     */
    private toggleAdvanceModeSteps(): void {
        const advancedMode: boolean = this.advancedMode === 'advanced';
        this.wizardService.setSubStepVisibleState(WizardSubStepTitlesEnum.FEEDBACK, advancedMode);
        this.wizardService.setSubStepVisibleState(WizardSubStepTitlesEnum.PERSISTENCE, advancedMode);
    }

    /**
     * Toggle the inputs that are only part of advanced mode.
     * @private
     */
    private toggleAdvanceModeInputs(): void {
        const advancedMode: boolean = this.advancedMode === 'advanced';
        const healthCheck: string = this.serviceForm.get(WizardSubStepTitlesEnum.HEALTH + '.healthCheck').value;
        const alwaysHiddenTypes: string[] = ['ping', 'noCheckOff', 'noCheckOn'];
        this.inputStateStore['healthCheckPort'] = { hidden: alwaysHiddenTypes.includes(healthCheck) || !advancedMode };
    }

    /**
     * Loads both the Health checks and fallback pages and set the values.
     * @private
     */
    private initLoading(): void {
        const apiCalls = {
            checkScripts: this.healthCheckScriptsApiService.getHealthChecksByService('layer-7'),
            backends: this.layer4ApiService.getBackends()
        };

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: object): void => {
                    const serviceError = this.getErrorForService(WizardTitleEnum.BACKENDS);
                    const template = this.getTemplate(WizardTitleEnum.BACKENDS, serviceError);

                    // Don't allow 'add another' if editing.
                    if (this.utilsService.isServiceId(template.id?.value)) {
                        this.wizardService.excludeFromAddAnother = WizardTitleEnum.BACKENDS;
                    }

                    this.setUpDropdownApiData(dataGroup);
                    const services: L4BackendServiceModel[] = this.formatServices(dataGroup['backends'], WizardTitleEnum.BACKENDS);
                    this.backends = this.filterServicesByName(template, services);

                    this.setInitialHealthCheckLookup(template);

                    this.patchTemplateToForm(template);

                    // Set timeout is here to wait until all form valueChanges have been triggered
                    // otherwise they override the setErrors.
                    setTimeout(() => {
                        if (serviceError?.error?.length) {
                            this.loadedWithErrors(serviceError.error);
                        }
                    }, 0);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('Layer4BackendComponent:initLoading', 'Error getting API data', error.message);
                }
            });
    }

    /**
     * Set up dropdowns with api data.
     * @param dataGroup
     * @private
     */
    private setUpDropdownApiData(dataGroup: object): void {
        if (dataGroup['checkScripts']?.length) {
            this.dropdown.checkScripts = this.getHealthCheckValues(dataGroup['checkScripts']);
        }
    }

    /**
     * Get the name and ids from the external health check scripts to populate dropdown.
     * @param healthchecks
     * @private
     */
    private getHealthCheckValues(healthchecks: any): DropdownModel[] {
        return this.utilsService.convertArrayToDropDown(healthchecks, 'id', 'name');
    }

    /**
     * Initialises the form build.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdownContent();

        // Populate our patch
        const templatePatch = this.createTemplatePatch(template);

        // Format name and prefix
        const newLabel = this.wizardService.wizardMode === 'partner'
            ? this.getFrontendName()
            : template.name.value;

        this.formatLabel(templatePatch, newLabel);

        // Add the frontend mode to the template patch before patching.
        const frontendMode: string = this.getFrontendMode();
        if (frontendMode) {
            templatePatch['mode'] = frontendMode;
        }

        const fallbackServerId: string = this.getFallbackServerId();
        if (fallbackServerId === '') {
            templatePatch['fallbackServerId'] = 'none';
        }

        this.advancedMode = this.wizardService.wizardMode;
        this.toggleAdvanceModeSteps();
        this.toggleAdvanceModeInputs();

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);
        this.registerSubSteps(WizardTitleEnum.BACKENDS);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        // Ensure the step is loaded in the correct width (mainly when going backwards in the wizard).
        const fullWidthPages: string[] = [WizardSubStepTitlesEnum.RIP_IDS];
        this.isFormFullWidth = fullWidthPages.includes(this.currentSubStepName);

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();
    }

    /**
     * Return dropdown structure array for mode
     * @private
     */
    private setUpDropdownContent(): void {
        this.dropdown.balanceModes = [
            <DropdownModel>{
                key: 'leastConnections',
                value: 'Weighted Least Connection'
            },
            <DropdownModel>{
                key: 'roundRobin',
                value: 'Weighted Round Robin'
            },
            <DropdownModel>{
                key: 'destinationHash',
                value: 'Destination Hash'
            }
        ];

        this.dropdown.forwardingModes = [
            <DropdownModel>{
                key: 'directRouting',
                value: 'Direct Routing'
            },
            <DropdownModel>{
                key: 'destinationNat',
                value: 'Destination Network Address Translation (DNAT)'
            },
            <DropdownModel>{
                key: 'sourceNat',
                value: 'Source Network Address Translation (SNAT)'
            },
            <DropdownModel>{
                key: 'tunnelling',
                value: 'Tunneling'
            }
        ];

        this.dropdown.healthChecks = [
            <DropdownModel>{
                key: 'connectToPort',
                value: 'Connect to port'
            },
            <DropdownModel>{
                key: 'externalScript',
                value: 'External Script'
            },
            <DropdownModel>{
                key: 'negotiateDns',
                value: 'Negotiate DNS'
            },
            <DropdownModel>{
                key: 'negotiateFtp',
                value: 'Negotiate FTP'
            },
            <DropdownModel>{
                key: 'negotiateHttp',
                value: 'Negotiate HTTP'
            },
            <DropdownModel>{
                key: 'negotiateHttpProxy',
                value: 'Negotiate HTTP (Proxy)'
            },
            <DropdownModel>{
                key: 'negotiateHttps',
                value: 'Negotiate HTTPS'
            },
            <DropdownModel>{
                key: 'negotiateImap',
                value: 'Negotiate IMAP'
            },
            <DropdownModel>{
                key: 'negotiateImaps',
                value: 'Negotiate IMAPS'
            },
            <DropdownModel>{
                key: 'negotiateLdap',
                value: 'Negotiate LDAP'
            },
            <DropdownModel>{
                key: 'negotiateMysql',
                value: 'Negotiate MySQL'
            },
            <DropdownModel>{
                key: 'negotiateNntp',
                value: 'Negotiate NNTP'
            },
            <DropdownModel>{
                key: 'negotiatePop',
                value: 'Negotiate POP'
            },
            <DropdownModel>{
                key: 'negotiatePops',
                value: 'Negotiate POPS'
            },
            <DropdownModel>{
                key: 'negotiateRadius',
                value: 'Negotiate RADIUS'
            },
            <DropdownModel>{
                key: 'negotiateSmtp',
                value: 'Negotiate SMTP'
            },
            <DropdownModel>{
                key: 'noCheckOff',
                value: 'No check, always off'
            },
            <DropdownModel>{
                key: 'noCheckOn',
                value: 'No check, always on'
            },
            <DropdownModel>{
                key: 'ping',
                value: 'Ping server'
            }
        ];

        this.dropdown.feedbackMethods = [
            <DropdownModel>{
                key: 'none',
                value: 'None'
            },
            <DropdownModel>{
                key: 'agent',
                value: 'Agent'
            },
            <DropdownModel>{
                key: 'http',
                value: 'HTTP'
            }
        ];
    }

    /**
     * Gets the first frontend mode from the frontendModes @Input.
     * @private
     */
    private getFrontendMode(): string {
        return this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.FRONTENDS}.${this.templateIndex}.mode.value`
        );
    }

    /**
     * Get the fallback server id from the controller state.
     * @private
     */
    private getFallbackServerId(): string {
        return this.utilsService.getObjectValue(
            this.wizardService.controllerState, `${WizardTitleEnum.BACKENDS}.${this.templateIndex}.fallbackServerId.value`
        );
    }

    /**
     * Output frontend form values as L7 VirtualServiceModel.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const backendOj = this.flattenServiceFormValues(<L4BackendServiceModel>{});

            // Unset the fallbackServerId if set to 'none'. Avoids api complaining it cannot find 'none'.
            if (backendOj.fallbackServerId === 'none') {
                backendOj.fallbackServerId = '';
            }

            // loop through form values
            const template: any = this.wizardService.controllerState[WizardTitleEnum.BACKENDS][this.templateIndex];
            for (const property in backendOj) {
                if (backendOj.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: backendOj[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }
}
